"use client";

import { useMutation, useQuery } from "@apollo/client";
import {
	ContactSettingsDocument,
	type ContactSettingsQuery,
	type ContactSettingsQueryVariables,
	SendContactDocument,
	type SendContactMutation,
	type SendContactMutationVariables,
} from "@graphql";
import { Button, Form, Skeleton, Typography, useNotifications, useZodForm } from "@tosize/ui";
import { useSearchParams } from "next/navigation";
import { type ComponentProps, useEffect } from "react";
import { z } from "zod";
import { useRouter } from "~/hooks/useRouter";
import { mapValidationErrorsToForm } from "~/lib/utils";
import { SubjectSelector } from "./form/SubjectSelector";
import type { TicketType } from "./types";

interface ContactFormProps extends ComponentProps<"div"> {
	lang: string;
	defaultType?: TicketType;
	resetTitle: string;
	errorMessage: string;
	emptyOptionMessage: string;
}

export function ContactForm(props: ContactFormProps) {
	const { children, defaultType, resetTitle, errorMessage, emptyOptionMessage, ...remainingProps } =
		props;
	const router = useRouter();
	const searchParams = useSearchParams();

	const form = useZodForm({
		schema: z.object({
			response_time: z.string().optional(),
			call_time: z.string().optional(),
			company: z.string().optional(),
			delivery_date: z.string().optional(),
			description: z.string().min(1),
			description_type: z.string().optional(),
			email: z.string().email().min(1),
			files: z.preprocess(
				(value) => (value instanceof FileList ? Array.from(value) : value),
				z.array(z.custom<File>((v) => v instanceof File)),
			),
			name: z.string().min(1),
			order_number: z.string().optional(),
			phone: z.string().optional(),
			solution: z.string().optional(),
			solution_other: z.string().optional(),
			type: z.string().optional(),
			url: z.string().optional(),
			product_type: z.string().min(1),
			product_materials: z.string().optional(),
			product_width: z.union([z.number().int().positive().min(1), z.nan()]).optional(),
			product_height: z.union([z.number().int().positive().min(1), z.nan()]).optional(),
			product_depth: z.union([z.number().int().positive().min(1), z.nan()]).optional(),
			product_thickness: z.union([z.number().int().positive().min(1), z.nan()]).optional(),
			product_urls: z.array(z.union([z.literal(""), z.string().trim().url()])).optional(),
		}),
		defaultValues: { files: [] },
	});

	const {
		setValue,
		setError,
		formState: { errors },
	} = form;

	const { data, loading } = useQuery<ContactSettingsQuery, ContactSettingsQueryVariables>(
		ContactSettingsDocument,
		{
			onCompleted(d) {
				const typeParam = searchParams?.get("type") ?? undefined;
				const newType = d?.contactSettings?.subjects?.find(
					(e) => e.category === typeParam,
				)?.category;

				setValue("type", newType ?? defaultType);
			},
		},
	);

	const [sendContact, { data: dataSend, reset }] = useMutation<
		SendContactMutation,
		SendContactMutationVariables
	>(SendContactDocument, { context: { clientName: "magento_upload" } });

	const { notify } = useNotifications();

	useEffect(() => {
		const typeParam = searchParams?.get("type") ?? undefined;
		const newType = data?.contactSettings?.subjects?.find(
			(e) => e.category === typeParam,
		)?.category;

		setValue("type", newType ?? defaultType);
	}, [searchParams, data?.contactSettings?.subjects?.find, defaultType, setValue]);

	return (
		<div {...remainingProps}>
			{loading && (
				<>
					<Skeleton className="mt-2 xs:hidden h-[42px]" />

					<div className="xs:grid hidden grid-cols-1 xs:grid-cols-2 gap-4 py-2 xs:py-4 sm:grid-cols-3">
						{Array.from({ length: 9 }).map((_value, index) => (
							<Skeleton className="h-10 w-full sm:h-14" key={index} />
						))}
					</div>
				</>
			)}
			{!loading && dataSend?.sendContact?.success !== true && (
				<Form
					id="contact-form"
					form={form}
					onSubmit={async (input) => {
						if (!Object.values(errors).length) {
							const phone = input.phone?.replace(/\s/g, "");
							const type = data?.contactSettings?.subjects?.find(
								(e) => e.category === input.type,
							)?.value;
							const product_urls = input.product_urls?.filter((e) => !!e);

							try {
								const sendContactData = await sendContact({
									variables: {
										input: { ...input, phone, type, product_urls, files: input.files as File[] },
									},
								});

								notify({
									variant: sendContactData.data?.sendContact?.success ? "success" : "danger",
									title: sendContactData.data?.sendContact?.message ?? "",
								});

								if (sendContactData.data?.sendContact?.success) {
									router.push("#main-content");
								}
							} catch (error) {
								mapValidationErrorsToForm(error, setError);
							}
						}
					}}
				>
					{!!data?.contactSettings?.subjects?.length && (
						<SubjectSelector
							subjects={data.contactSettings.subjects}
							emptyOptionMessage={emptyOptionMessage}
							errorMessage={errorMessage}
						/>
					)}

					{children}
				</Form>
			)}
			{!loading && !!dataSend?.sendContact?.success && (
				<>
					<Typography className="mb-4 text-center">{dataSend.sendContact.message}</Typography>
					<Button
						type="reset"
						onClick={() => {
							reset();
							reset();
						}}
						className="flex w-full items-center justify-center"
						color="primary"
						size="large"
					>
						{resetTitle}
					</Button>
				</>
			)}
		</div>
	);
}
